import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { SynopticPlanItem } from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { CableDirectionEnum, PlanTypeEnum, SynopticBoxPlanFiber } from '@connect-services/client/sdk/api';
import { SynopticBayPlanEditService } from '@connect-services/client/services/synoptic/edit/synoptic-bay-plan-edit.service';
import { SynopticPlanEditService } from '@connect-services/client/services/synoptic/edit/synoptic-plan-edit.service';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    selector: 'app-side-fiber-edit',
    styleUrl: './side-fiber-edit.component.scss',
    templateUrl: './side-fiber-edit.component.html',
})
export class SideFiberEditComponent {
    public readonly cableCode = input.required<string>();
    public readonly cassettes = input.required<SynopticPlanItem[][]>();
    public readonly fiber = input.required<SynopticBoxPlanFiber>();
    public readonly planType = input.required<PlanTypeEnum>();
    public readonly side = input.required<CableDirectionEnum>();
    public readonly tubeNumber = input.required<number>();

    protected readonly CableDirectionEnum = CableDirectionEnum;
    protected readonly isFiberLinked = computed<boolean>(() => {
        const isLeft = this.side() === CableDirectionEnum.LEFT;
        let cassettes = this.cassettes();
        if (this.planType() === PlanTypeEnum.BAY) {
            // For bay plan, we check assignment of cassettes of all drawers, not only the current one
            cassettes = this.synopticBayPlanEditService.cassettes();
        }

        return cassettes.flat().some((cassetteItem: SynopticPlanItem) => {
            const itemCableCode = isLeft ? cassetteItem.cable_entrant : cassetteItem.cable_sortant;
            const itemTubeNumber = isLeft ? cassetteItem.numero_tube_entrant : cassetteItem.numero_tube_sortant;
            const itemFiberCode = isLeft ? cassetteItem.fibre_entrante : cassetteItem.fibre_sortante;

            return (
                itemCableCode === this.cableCode() &&
                itemTubeNumber === this.tubeNumber().toString() &&
                itemFiberCode === this.fiber().code
            );
        });
    });
    protected readonly isFiberSelected = computed<boolean>(() => {
        const selectedElement = this.synopticPlanEditService.selectedCableFiber();

        return (
            selectedElement?.cableCode === this.cableCode() &&
            selectedElement.tubeIndex === this.tubeNumber() &&
            selectedElement.fiberCode === this.fiber().code
        );
    });
    protected readonly isSelectionEnabled = computed<boolean>(() => {
        const cassettePositionSelected = this.synopticPlanEditService.selectedCassettePosition();

        return !this.isFiberLinked() && (!cassettePositionSelected || cassettePositionSelected.side === this.side());
    });

    private readonly synopticBayPlanEditService = inject(SynopticBayPlanEditService);
    private readonly synopticPlanEditService = inject(SynopticPlanEditService);

    protected selectFiber(): void {
        if (this.isFiberSelected()) {
            this.synopticPlanEditService.selectedCableFiber.set(null);

            return;
        }

        this.synopticPlanEditService.selectedCableFiber.set({
            cableCode: this.cableCode(),
            fiberCode: this.fiber().code,
            side: this.side(),
            tubeIndex: this.tubeNumber(),
        });
    }
}
